import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { logSiteModuleConfigSave } from '@/utils/conferenceLogger';
import { Link, useParams } from 'react-router-dom';
import { Layout, Button, Dropdown, Menu, Tooltip, Modal, message, Alert, Space, Typography } from 'antd';
import { isEmpty } from 'lodash';
import copy from 'copy-to-clipboard';
import QRCode from 'qrcode.react';
import './SiteModules.less';
import definedSiteModules from '../site_module_types'; // 系统定义的网站模块
import HomeModuleDrawer from './components/HomeModuleDrawer';
import AddSiteModuleDrawer from './components/AddSiteModuleDrawer';
import OrganizationModuleDrawer from './components/OrganizationModuleDrawer';
import JournalModuleDrawer from './components/JournalModuleDrawer';
import FileModuleDrawer from './components/FileModuleDrawer';
import AgendaModuleDrawer from './components/AgendaModuleDrawer';
import GuestModuleDrawer from './components/GuestModuleDrawer';
import LiveModuleDrawer from './components/LiveModuleDrawer';
import NewsModuleDrawer from './components/NewsModuleDrawer';
import ReviewModuleDrawer from './components/ReviewModuleDrawer';
import ExhibitionModuleDrawer from './components/ExhibitionModuleDrawer';
import PartnerModuleDrawer from './components/PartnerModuleDrawer';
import RichTextModuleDrawer from './components/RichTextModuleDrawer';
import ExternalLinkModuleDrawer from './components/ExternalLinkModuleDrawer';
import MyprojectsModuleDrawer from './components/MyprojectsModuleDrawer';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getSiteModulesApi, updateSiteModulesApi, sortSiteModulesApi, deleteSiteModuleApi, setDefaultShowApi } from '@services/SiteModule';
import { conferenceOnceApi } from '@services/Conference';
import config from '@config';
import { deleteConferenceCache } from '../../../services/Conference';
import PageHeader from '@components/PageHeader/PageHeader';
import { OperationLogDrawerHostProvider } from '@/components/common/operationLogDrawerHostContext';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
/** 网站模块列表可排序行（拖拽手柄触发；首页 moduleType===1 禁用排序） */
function SortableSiteModuleRow({ module, renderMoreMenu, defaultAccessSetting, showModuleSettingDrawer, showModuleQrCode }) {
    const isHomeModule = module.moduleType === 1;
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: module.id,
        disabled: isHomeModule,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className="conference-site_module">
                {!isHomeModule && (
                    <Tooltip title="网站模块排序">
                        <Button type="text" size="small" {...listeners}>
                            <i className="bi bi-grip-vertical"></i>
                        </Button>
                    </Tooltip>
                )}

                <div className="site-module-title">
                    <span dangerouslySetInnerHTML={{ __html: module.displayTitle }}></span>
                </div>

                {!isHomeModule && (
                    <div className="site-module-state">
                        <span className={`state-item ${Number(module.status) === 1 ? 'state-success' : 'state-warning'}`}>导航：{Number(module.status) === 1 ? '开启' : '敬请期待'}</span>
                        <span className={`state-item ${Number(module.hiddenWeb) === 0 ? 'state-success' : 'state-error'}`}>Web端：{Number(module.hiddenWeb) === 0 ? '显示' : '隐藏'}</span>
                        <span className={`state-item ${Number(module.hiddenH5) === 0 ? 'state-success' : 'state-error'}`}>H5端：{Number(module.hiddenH5) === 0 ? '显示' : '隐藏'}</span>
                    </div>
                )}

                <div className="site-module-operations">
                    <Button className={`access-btn ${Number(module.defaultShow) === 1 && 'text-btn'}`} type={Number(module.defaultShow) === 1 ? 'primary' : 'default'} size="small" onClick={() => defaultAccessSetting(module)}>
                        {Number(module.defaultShow) === 1 ? '默认访问' : '设为默认'}
                    </Button>

                    <div className="link-btns">
                        <Tooltip title="网站模块配置">
                            <Button type="link" size="small" onClick={() => showModuleSettingDrawer(module)}>
                                模块配置
                            </Button>
                        </Tooltip>

                        <Tooltip title="下载官网模块二维码">
                            <Button type="link" size="small" onClick={() => showModuleQrCode(module)}>
                                二维码
                            </Button>
                        </Tooltip>

                        <Dropdown popupRender={() => renderMoreMenu(module)} trigger={['hover']} placement="bottomRight">
                            <Button type="text" size="small" className="more-btn">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function SiteModules() {
    const params = useParams();
    const conferenceId = params?.id || 0; // 会议项目 ID

    const [conference, setConference] = useState(null);

    const getConferenceInfo = async conferenceId => {
        const res = await conferenceOnceApi({ id: conferenceId });
        if (res.status_code === 200) {
            setConference(res.data);
        }
    };

    useEffect(() => {
        if (!!conferenceId) {
            getConferenceInfo(conferenceId);
        }
    }, [conferenceId]);

    /**
     * @author WisdomFusion<at>gmail<dot>com
     * @date 2023/4/2
     *
     * 网站模块：
     * 1. 网站模块列表全部展示，无须分页
     * 2. 新增网站模块
     * 2.0 新增模块，导航状态（status）默认为2敬请期待，默认隐藏状态hidden=1
     * 2.1 会议项目时，默认增加“官网首页”模块
     * 2.2 内置模块（builtin=1）访问路径不可能修改，非内置模块（图文模块、外部链接模块）访问路径自定义
     * 2.3 外部链接模块，多一个“外部链接”字段
     * 3. 可对所有模块进行编辑（模块配置）
     * 4. 官网首页不可排序不可删除，其他模块可
     * 5. 内置模块（非自定义模块）在同一个会议项目中，只能添加一次，前端逻辑已处理，比如，直播模块只能添加一次
     * 6. 配置网站模块
     * 6.1 ...
     *
     */

    // 从系统预定义的网站模块列表中，把 title, name 和 builtin 字段补全
    const prepareSiteModules = siteModules =>
        siteModules.map(module => {
            definedSiteModules.forEach(dm => {
                if (dm.moduleType === module.moduleType) {
                    module = {
                        ...module,
                        title: dm.title,
                        name: dm.name,
                        builtin: dm.builtin,
                    };
                }
            });
            return module;
        });

    // 通过 moduleType 获取预定义网站模块
    const getSiteModuleByType = moduleType => {
        let siteModule = null;
        definedSiteModules.forEach(m => {
            if (m.moduleType === moduleType) {
                siteModule = m;
            }
        });
        return siteModule;
    };

    // 当前选中模块
    const [currentModule, setCurrentModule] = useState(null);

    // 新增模块 Drawer
    const [visibleAddSiteModuleDrawer, setVisibleAddSiteModuleDrawer] = useState(false);

    const showAddSiteModuleDrawer = () => {
        setVisibleAddSiteModuleDrawer(true);
    };

    // drawerState: {moduleType: 1, visible: true}
    // moduleType 模块类型
    // visible: 显示
    const [drawerState, setDrawerState] = useState(null);
    const siteModuleBeforeRef = useRef(null);

    /** 模块 Drawer 在表单/编辑器回显完成后，注册与「确定」提交同构的 baseline */
    const registerSiteModuleSubmitBaseline = useCallback(payload => {
        siteModuleBeforeRef.current = payload;
    }, []);

    // 根据 drawerState 设定，显示不同模块类型的配置 drawer
    const showModuleSettingDrawer = module => {
        if (!isEmpty(module)) {
            console.log(module);
            // 首页模块依赖 Drawer 内表单回显后再注册 baseline；其余模块可直接用列表项
            siteModuleBeforeRef.current = module.moduleType === 1 ? null : JSON.parse(JSON.stringify(module));
            setCurrentModule(module);

            setDrawerState({ moduleType: module.moduleType, visible: true });
        }
    };

    // 网站模块列表数据
    const [siteModuleList, setSiteModuleList] = useState([]);

    const [visibleModuleQrcode, setVisibleModuleQrcode] = useState(false);

    const showModuleQrCode = module => {
        if (!isEmpty(module)) {
            console.log(module);
            setCurrentModule(module);
            setVisibleModuleQrcode(true);
        }
    };

    const closeModuleQrcode = () => setVisibleModuleQrcode(false);

    const downloadQRCode = () => {
        const qrCode = document.getElementById(`qrcode_conf_${conference.id}`);
        const image = new Image();
        image.src = qrCode.toDataURL('image/png');
        const link = document.getElementById(`link_qrcode_conf_${conference.id}`);
        link.href = image.src;
    };

    const copyText = text => {
        copy(text);
        message.success('复制成功');
    };

    // 网站模块刪除
    const deleteSiteModule = id => {
        Modal.confirm({
            title: '提示',
            content: '是否确认删除当前模块',
            cancelText: '取消',
            okText: '确认',
            closable: true,
            onOk: async () => {
                const res = await deleteSiteModuleApi({ id });
                if (res.status_code === 200) {
                    message.success('删除成功');
                    getSiteModules();
                }
            },
        });
    };

    // 编辑配置模块
    const updateModuleSettings = async module => {
        console.log(module);
        const before = siteModuleBeforeRef.current;
        const params = {
            ...module,
            conferenceId,
        };
        const logSave = async isSuccess => {
            try {
                await logSiteModuleConfigSave({
                    entityId: module.id,
                    moduleLabel: module.displayTitle,
                    moduleType: module.moduleType,
                    conferenceId,
                    before,
                    after: params,
                    isSuccess,
                    errorMessage: isSuccess ? null : undefined,
                });
            } catch (logError) {
                console.error('操作日志上报失败', logError);
            }
        };
        try {
            const res = await updateSiteModulesApi(params);
            if (res.status_code === 200) {
                void logSave(true);
                message.success('配置模块成功');
                setDrawerState({ moduleType: module.moduleType, visible: false });
                await getSiteModules();
            } else {
                void logSiteModuleConfigSave({
                    entityId: module.id,
                    moduleLabel: module.displayTitle,
                    moduleType: module.moduleType,
                    conferenceId,
                    before,
                    after: params,
                    isSuccess: false,
                    errorMessage: res.message,
                }).catch(logError => console.error('操作日志上报失败', logError));
            }
        } catch (error) {
            void logSiteModuleConfigSave({
                entityId: module.id,
                moduleLabel: module.displayTitle,
                moduleType: module.moduleType,
                conferenceId,
                before,
                after: params,
                isSuccess: false,
                errorMessage: error?.message,
            }).catch(logError => console.error('操作日志上报失败', logError));
        }
    };

    // 网站模块排序（@dnd-kit，避免 react-sortable-hoc 使用 findDOMNode）
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        }),
    );

    const onDragEndHandler = event => {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = siteModuleList.findIndex(m => m.id === active.id);
        const newIndex = siteModuleList.findIndex(m => m.id === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        const newData = arrayMove(siteModuleList, oldIndex, newIndex);
        console.log(newData, '排序');
        setSiteModuleList(newData);
        const ids = newData.map(item => item.id);
        sortSiteModulesApi({ resList: [{ ids: ids }] });
    };

    // 设置默认访问
    const defaultAccessSetting = async item => {
        if (Number(item.defaultShow) === 1) {
            return;
        }
        const res = await setDefaultShowApi({
            defaultShow: 1,
            siteModuleId: item.id,
            conferenceId: conferenceId,
        });
        if (res.status_code === 200) {
            message.success('设置成功');
            getSiteModules();
        }
    };

    const getPageLink = (platform, module) => {
        let link = '';

        if (module.moduleType === 1) {
            link = `${config.HUIYI_DOMAIN}/${platform}/${conference?.slug}`;
        } else if (module.moduleType === 21 && module.url) {
            link = module.url;
        } else {
            link = `${config.HUIYI_DOMAIN}/${platform}/${conference?.slug}/${module.path}`;
        }

        return link;
    };

    const renderMoreMenu = module => (
        <Menu>
            <Menu.Item key="copyPc" onClick={() => copyText(getPageLink('web', module))}>
                复制PC链接
            </Menu.Item>
            <Menu.Item key="copyH5" onClick={() => copyText(getPageLink('h5', module))}>
                复制H5链接
            </Menu.Item>
            {module.moduleType !== 1 && (
                <Menu.Item key="delete" danger onClick={() => deleteSiteModule(module.id)}>
                    删除网站模块
                </Menu.Item>
            )}
        </Menu>
    );

    // 获取网站列表
    const getSiteModules = async () => {
        const res = await getSiteModulesApi({ conferenceId: conferenceId });
        if (res.status_code === 200) {
            const newList = prepareSiteModules(res.data);
            setSiteModuleList(newList);
        }
    };

    useEffect(() => {
        if (conferenceId !== undefined) {
            getSiteModules();
        }
    }, [conferenceId]);

    const clearConferenceSiteModuleCache = async () => {
        const data = {
            type: 'conference',
            key: `conference_${conferenceId}_site_modules`,
        };
        const res = await deleteConferenceCache(data);
        if (res?.status_code === 200) {
            message.success('更新会议官网模块成功！');
        }
    };

    const onClearConferenceSiteModuleCache = () => {
        if (conferenceId) {
            Modal.confirm({
                title: '更新会议官网',
                icon: <i className="bi bi-exclamation-circle"></i>,
                width: 480,
                centered: true,
                content: (
                    <>
                        <p>为减轻性能压力，系统自动缓存数据，该功能将更新用户端数据，展现最新设定内容。</p>
                        <p>
                            <Typography.Text type="danger">会议进行期间谨慎更新！</Typography.Text>
                        </p>
                    </>
                ),
                onOk() {
                    clearConferenceSiteModuleCache();
                },
                cancelText: '取消，暂不更新',
                okText: '知晓，确认更新',
            });
        }
    };

    useEffect(() => {
        document.title = '网站模块-医会议';
    }, []);

    const webVisibleCount = useMemo(() => siteModuleList.filter(module => module.moduleType !== 1 && Number(module.hiddenWeb) === 0).length, [siteModuleList]);
    const h5VisibleCount = useMemo(() => siteModuleList.filter(module => module.moduleType !== 1 && Number(module.hiddenH5) === 0).length, [siteModuleList]);

    return (
        <OperationLogDrawerHostProvider>
            <PageHeader title="会议官网" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '会议项目', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '会议官网']} />

            <div className="page-route-body">
                <div className="main-toolbar">
                    <div className="main-tools">
                        <Space>
                            <Button type="primary" onClick={() => showAddSiteModuleDrawer()}>
                                新增模块
                            </Button>
                        </Space>
                    </div>

                    <div className="sub-tools">
                        <Space>
                            {/*<Button onClick={() => showSiteQrcode()}>会议官网二维码</Button>*/}
                            <Button onClick={() => onClearConferenceSiteModuleCache()}>更新会议官网模块</Button>
                        </Space>
                    </div>
                </div>

                <Alert
                    style={{ marginBottom: 16 }}
                    title={
                        <>
                            <div>1. 会议官网初次建站时，请完成“首页”模块配置，再进行其他模块创建及配置，首页未配置官网无法正常显示；</div>
                            <div>2. 如果希望用户在访问官网时，默认访问指定“模块”，请将该“模块”设为默认访问。</div>
                        </>
                    }
                />

                <div className="site-module-summary">
                    <Space size={24}>
                        <span>Web端展示模块：{webVisibleCount} 个</span>
                        <span>H5端展示模块：{h5VisibleCount} 个</span>
                    </Space>
                </div>

                <div className="site-modules">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndHandler}>
                        <SortableContext items={siteModuleList.map(m => m.id)} strategy={verticalListSortingStrategy}>
                            {siteModuleList.map(module => (
                                <SortableSiteModuleRow key={module.id} module={module} renderMoreMenu={renderMoreMenu} defaultAccessSetting={defaultAccessSetting} showModuleSettingDrawer={showModuleSettingDrawer} showModuleQrCode={showModuleQrCode} />
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>
            </div>

            <Modal title="下载官网模块二维码" centered open={visibleModuleQrcode} onCancel={closeModuleQrcode} footer={null}>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                    {conference && conference.slug && !isEmpty(currentModule) && (
                        <>
                            <QRCode id={`qrcode_conf_${conference?.id}`} size={300} value={getPageLink('h5', currentModule)} />
                        </>
                    )}
                    <div style={{ textAlign: 'center', paddingTop: 10 }}>
                        <a id={`link_qrcode_conf_${conference?.id}`} download={`会议官网二维码_${conference?.name}_${currentModule?.displayTitle}`} onClick={() => downloadQRCode()}>
                            {`下载${currentModule?.displayTitle}二维码`}
                        </a>
                    </div>
                </div>
            </Modal>

            <AddSiteModuleDrawer visibleAddSiteModuleDrawer={visibleAddSiteModuleDrawer} setVisibleAddSiteModuleDrawer={setVisibleAddSiteModuleDrawer} preparedSiteModules={siteModuleList} getSiteModuleByType={getSiteModuleByType} getSiteModules={getSiteModules} />

            <HomeModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} registerSubmitBaseline={registerSiteModuleSubmitBaseline} />

            <OrganizationModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <JournalModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <FileModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <AgendaModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <GuestModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <LiveModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <NewsModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <ReviewModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <ExhibitionModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <PartnerModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <RichTextModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <ExternalLinkModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />

            <MyprojectsModuleDrawer drawerState={drawerState} setDrawerState={setDrawerState} currentModule={currentModule} updateModuleSettings={updateModuleSettings} />
        </OperationLogDrawerHostProvider>
    );
}
